import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, TrendingUp, TrendingDown, CheckCircle2, XCircle, 
  Clock, Award, Building2, Calendar, Loader2, AlertCircle,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TicketAnalyticsDashboard = ({ businessId = null, isAdmin = false }) => {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(30);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [businessId, period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = businessId ? { 'X-Business-Id': businessId } : {};
      const response = await axios.get(`${API}/analytics/tickets/summary`, {
        params: { days: period },
        headers
      });
      setAnalytics(response.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(t('ticket_analytics.error_loading'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-2xl p-6 shadow-card border border-border/50">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-2xl p-6 shadow-card border border-border/50">
        <div className="flex items-center gap-3 text-amber-600">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const summary = analytics?.summary || {};
  const successRate = summary.success_rate || 0;
  const isPositive = successRate >= 70;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-2xl p-6 shadow-card border border-border/50"
      data-testid="ticket-analytics-dashboard"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-violet-500 rounded-xl flex items-center justify-center text-white">
            <BarChart3 className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">{t('ticket_analytics.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('ticket_analytics.subtitle')}</p>
          </div>
        </div>
        
        {/* Period Selector */}
        <div className="flex gap-2">
          {[7, 30, 90].map((days) => (
            <Button
              key={days}
              variant={period === days ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPeriod(days)}
              className={period === days ? 'bg-violet-500 hover:bg-violet-600' : ''}
            >
              {days}d
            </Button>
          ))}
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {/* Total Tickets */}
        <div className="bg-muted/30 rounded-xl p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Calendar className="w-4 h-4" />
            <span className="text-xs">{t('ticket_analytics.total_tickets')}</span>
          </div>
          <p className="text-2xl font-bold">{summary.total_tickets || 0}</p>
        </div>

        {/* Success Rate */}
        <div className={`rounded-xl p-4 ${isPositive ? 'bg-green-50 dark:bg-green-950/20' : 'bg-red-50 dark:bg-red-950/20'}`}>
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            {isPositive ? <TrendingUp className="w-4 h-4 text-green-600" /> : <TrendingDown className="w-4 h-4 text-red-600" />}
            <span className="text-xs">{t('ticket_analytics.success_rate')}</span>
          </div>
          <p className={`text-2xl font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {successRate}%
          </p>
        </div>

        {/* Verified Success */}
        <div className="bg-green-50 dark:bg-green-950/20 rounded-xl p-4">
          <div className="flex items-center gap-2 text-green-700 dark:text-green-400 mb-1">
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-xs">{t('ticket_analytics.verified')}</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{summary.verified_success || 0}</p>
        </div>

        {/* Rejected */}
        <div className="bg-red-50 dark:bg-red-950/20 rounded-xl p-4">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-400 mb-1">
            <XCircle className="w-4 h-4" />
            <span className="text-xs">{t('ticket_analytics.rejected')}</span>
          </div>
          <p className="text-2xl font-bold text-red-600">{summary.verified_rejected || 0}</p>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* Total Points */}
        <div className="bg-amber-50 dark:bg-amber-950/20 rounded-xl p-4">
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 mb-1">
            <Award className="w-4 h-4" />
            <span className="text-xs">{t('ticket_analytics.points_awarded')}</span>
          </div>
          <p className="text-2xl font-bold text-amber-600">{summary.total_points_awarded?.toLocaleString() || 0}</p>
        </div>

        {/* AI Confidence */}
        <div className="bg-violet-50 dark:bg-violet-950/20 rounded-xl p-4">
          <div className="flex items-center gap-2 text-violet-700 dark:text-violet-400 mb-1">
            <BarChart3 className="w-4 h-4" />
            <span className="text-xs">{t('ticket_analytics.ai_confidence')}</span>
          </div>
          <p className="text-2xl font-bold text-violet-600">{summary.avg_ai_confidence || 0}%</p>
        </div>
      </div>

      {/* Pending */}
      {summary.pending_verification > 0 && (
        <div className="bg-blue-50 dark:bg-blue-950/20 rounded-xl p-4 mb-6 flex items-center gap-3">
          <Clock className="w-5 h-5 text-blue-600" />
          <div>
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {summary.pending_verification} {t('ticket_analytics.pending_verification')}
            </p>
            <p className="text-xs text-blue-600">{t('ticket_analytics.pending_desc')}</p>
          </div>
        </div>
      )}

      {/* Expandable Details */}
      <Button
        variant="ghost"
        className="w-full justify-between"
        onClick={() => setShowDetails(!showDetails)}
        data-testid="toggle-analytics-details"
      >
        <span>{t('ticket_analytics.show_details')}</span>
        {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </Button>

      {showDetails && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mt-4 space-y-4"
        >
          {/* Top Businesses */}
          {analytics?.top_businesses?.length > 0 && (
            <div>
              <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                {t('ticket_analytics.top_businesses')}
              </h4>
              <div className="space-y-2">
                {analytics.top_businesses.slice(0, 5).map((business, idx) => (
                  <div key={business.business_id} className="flex items-center justify-between bg-muted/30 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-amber-500 text-white' :
                        idx === 1 ? 'bg-gray-400 text-white' :
                        idx === 2 ? 'bg-amber-700 text-white' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {idx + 1}
                      </span>
                      <span className="font-medium text-sm">{business.business_name}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold">{business.total_verifications}</p>
                      <p className="text-xs text-muted-foreground">{business.points_awarded} pts</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rejection Reasons */}
          {analytics?.rejection_reasons?.length > 0 && (
            <div>
              <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-500" />
                {t('ticket_analytics.rejection_reasons')}
              </h4>
              <div className="space-y-2">
                {analytics.rejection_reasons.slice(0, 5).map((reason, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-red-50 dark:bg-red-950/20 rounded-lg p-3">
                    <span className="text-sm text-red-800 dark:text-red-200 truncate flex-1 mr-4">
                      {reason.reason}
                    </span>
                    <span className="text-sm font-bold text-red-600">{reason.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

export default TicketAnalyticsDashboard;
